/**
 * URL helpers for the Contacts/Organizations hub and detail pages.
 *
 * All paths are scoped under `/i/:instanceId/...`. We detect the instance
 * prefix from `window.location.pathname` to stay consistent with the lead
 * helpers in `leadRoutes.ts`.
 */

import type { ContactKind, OrganizationType } from '../types';
import { projectsListPath } from './projectRoutes';

const instancePrefix = (): string => {
  if (typeof window === 'undefined') return '';
  const match = window.location.pathname.match(/^\/i\/[^/]+/);
  return match ? match[0] : '';
};

export type ContactsHubTab = 'organizations' | 'contacts';

const ORGANIZATION_TYPE_TO_SLUG: Record<OrganizationType, string> = {
  'Gewerblicher Kunde': 'gewerblicher-kunde',
  Lieferant: 'lieferant',
  Partner: 'partner',
};

const SLUG_TO_ORGANIZATION_TYPE = new Map<string, OrganizationType>(
  Object.entries(ORGANIZATION_TYPE_TO_SLUG).map(([type, slug]) => [slug, type as OrganizationType]),
);

const CONTACT_KIND_TO_SLUG: Record<ContactKind, string> = {
  Privatkunde: 'privatkunde',
  'Gewerblicher Kontakt': 'gewerblicher-kontakt',
};

const SLUG_TO_CONTACT_KIND = new Map<string, ContactKind>(
  Object.entries(CONTACT_KIND_TO_SLUG).map(([kind, slug]) => [slug, kind as ContactKind]),
);

export function organizationTypeToSlug(type: OrganizationType): string {
  return ORGANIZATION_TYPE_TO_SLUG[type];
}

export function organizationTypeFromSlug(slug: string | null | undefined): '' | OrganizationType {
  if (!slug) return '';
  return SLUG_TO_ORGANIZATION_TYPE.get(slug) ?? '';
}

export function contactKindToSlug(kind: ContactKind): string {
  return CONTACT_KIND_TO_SLUG[kind];
}

export function contactKindFromSlug(slug: string | null | undefined): '' | ContactKind {
  if (!slug) return '';
  return SLUG_TO_CONTACT_KIND.get(slug) ?? '';
}

export function contactsHubPath(
  tab: ContactsHubTab = 'organizations',
  subFilter: '' | OrganizationType | ContactKind = '',
): string {
  const q = new URLSearchParams();
  if (tab === 'contacts') q.set('tab', 'contacts');
  if (subFilter) {
    if (tab === 'organizations' && subFilter in ORGANIZATION_TYPE_TO_SLUG) {
      q.set('type', organizationTypeToSlug(subFilter as OrganizationType));
    } else if (tab === 'contacts' && subFilter in CONTACT_KIND_TO_SLUG) {
      q.set('kind', contactKindToSlug(subFilter as ContactKind));
    }
  }
  const qs = q.toString();
  return `${instancePrefix()}/contacts${qs ? `?${qs}` : ''}`;
}

export function contactDetailPath(contactId: string | number): string {
  return `${instancePrefix()}/contacts/${encodeURIComponent(String(contactId))}`;
}

export function organizationDetailPath(orgId: string): string {
  return `${instancePrefix()}/organizations/${encodeURIComponent(orgId)}`;
}

/**
 * URL that opens the lead-creation modal pre-filled with a contact or
 * organization. The Leads page reads this query param and opens the
 * CreateLeadModal.
 */
export function createProjectForContactPath(contactId: string | number): string {
  return `${projectsListPath('pipeline')}?createFor=contact:${encodeURIComponent(String(contactId))}`;
}

export function createProjectForOrganizationPath(orgId: string): string {
  return `${projectsListPath('pipeline')}?createFor=organization:${encodeURIComponent(orgId)}`;
}

/** @deprecated Use `createProjectForContactPath`. */
export const createLeadForContactPath = createProjectForContactPath;

/** @deprecated Use `createProjectForOrganizationPath`. */
export const createLeadForOrganizationPath = createProjectForOrganizationPath;
